import { useEffect, useMemo, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import {
  beginPageSizeMeasurement,
  fingerprintPageSizes,
  invalidatePageSizeSync,
} from '../utils/pageSizeEpoch';
import { parsePdfUserError } from '../utils/pdfUserError';

export interface PdfPageSize {
  width: number;
  height: number;
  rotation?: number;
}

/** Server upload / mutation metadata — skip full pdfjs page loop when complete. */
export interface PdfDocumentSeed {
  pageCount: number;
  pageSizes: PdfPageSize[];
}

export interface PdfDocumentState {
  doc: pdfjsLib.PDFDocumentProxy | null;
  pageCount: number;
  pageSizes: PdfPageSize[];
  pageSizesReady: boolean;
  /** Monotonic token — bumps each time pdf.js finishes measuring the open file. */
  sizesEpoch: number;
  loading: boolean;
  error: string | null;
}

const DEFAULT_PAGE_SIZE: PdfPageSize = { width: 612, height: 792, rotation: 0 };
const SIZE_CHUNK = 8;

function isMeasured(size: PdfPageSize | undefined): boolean {
  return !!size && size.width > 0 && size.height > 0;
}

function seedIsComplete(seed: PdfDocumentSeed | null | undefined): boolean {
  if (!seed || seed.pageCount <= 0) return false;
  if (seed.pageSizes.length !== seed.pageCount) return false;
  return seed.pageSizes.every(isMeasured);
}

function buildInitialSizes(count: number, seed: PdfDocumentSeed | null | undefined): PdfPageSize[] {
  const fallback = seed?.pageSizes.find(isMeasured) ?? DEFAULT_PAGE_SIZE;
  const out: PdfPageSize[] = [];
  for (let i = 0; i < count; i++) {
    const fromSeed = seed?.pageSizes[i];
    out.push(isMeasured(fromSeed) ? { ...fromSeed! } : { ...fallback });
  }
  return out;
}

function allSizesMeasured(sizes: PdfPageSize[]): boolean {
  return sizes.length > 0 && sizes.every(isMeasured);
}

async function measurePage(pdf: pdfjsLib.PDFDocumentProxy, pageNumber: number): Promise<PdfPageSize> {
  const page = await pdf.getPage(pageNumber);
  const viewport = page.getViewport({ scale: 1.0 });
  const rot = (page as { rotate?: number }).rotate ?? 0;
  return { width: viewport.width, height: viewport.height, rotation: rot };
}

function idle(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(() => resolve(), { timeout: 120 });
    } else {
      setTimeout(resolve, 0);
    }
  });
}

async function fillMissingPageSizes(
  pdf: pdfjsLib.PDFDocumentProxy,
  sizes: PdfPageSize[],
  onUpdate: (next: PdfPageSize[]) => void,
  isCancelled: () => boolean
): Promise<PdfPageSize[]> {
  const count = sizes.length;
  const latest = [...sizes];
  for (let start = 0; start < count; start += SIZE_CHUNK) {
    if (isCancelled()) return latest;
    const end = Math.min(start + SIZE_CHUNK, count);
    let changed = false;
    for (let i = start; i < end; i++) {
      if (isCancelled()) return latest;
      try {
        const measured = await measurePage(pdf, i + 1);
        if (
          latest[i].width !== measured.width ||
          latest[i].height !== measured.height ||
          (latest[i].rotation ?? 0) !== (measured.rotation ?? 0)
        ) {
          latest[i] = measured;
          changed = true;
        }
      } catch {
        // keep placeholder for this page
      }
    }
    if (changed) onUpdate([...latest]);
    await idle();
  }
  return latest;
}

function seedFingerprint(seed: PdfDocumentSeed | null | undefined): string {
  if (!seed) return '';
  return `${seed.pageCount}:${fingerprintPageSizes(seed.pageSizes)}`;
}

/** Single pdfjs load per open file — shared by PdfViewer and PageNavigator. */
export function usePdfDocument(
  file: File | null,
  seed?: PdfDocumentSeed | null
): PdfDocumentState {
  const [doc, setDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [pageSizes, setPageSizes] = useState<PdfPageSize[]>([]);
  const [pageSizesReady, setPageSizesReady] = useState(true);
  const [sizesEpoch, setSizesEpoch] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seedKey = useMemo(() => seedFingerprint(seed), [seed?.pageCount, seed?.pageSizes]);

  useEffect(() => {
    if (!file) {
      setDoc(null);
      setPageCount(0);
      setPageSizes([]);
      setPageSizesReady(true);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    let loadedDoc: pdfjsLib.PDFDocumentProxy | null = null;
    // File changed A->B: the cleanup below destroys pdf_A, but until the new doc
    // resolves `doc` would still point at the destroyed proxy, so PdfPage could call
    // getPage() on it. Clear doc + derived ready state up front so consumers see a
    // loading state rather than a destroyed document.
    setDoc(null);
    setPageCount(0);
    setPageSizes([]);
    // Drop viewport syncs from any prior measurement pass (e.g. seed arrives after file).
    invalidatePageSizeSync();
    const measureEpoch = beginPageSizeMeasurement();
    // Invalidate downstream sync immediately — do not wait for async pdf.js load.
    setSizesEpoch(measureEpoch);
    setPageSizesReady(false);
    setLoading(true);
    setError(null);

    const completeSeed = seedIsComplete(seed);

    (async () => {
      try {
        const arrayBuffer = await file.arrayBuffer();
        const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
        const pdf = await loadingTask.promise;
        loadedDoc = pdf;

        if (cancelled) {
          pdf.destroy().catch(() => {});
          return;
        }

        const count = seed?.pageCount && seed.pageCount > 0 ? seed.pageCount : pdf.numPages;
        const initialSizes = completeSeed
          ? seed!.pageSizes.map((s) => ({ ...s }))
          : buildInitialSizes(count, seed);

        setDoc(pdf);
        setPageCount(count);
        setPageSizes(initialSizes);

        const finalSizes = await fillMissingPageSizes(
          pdf,
          initialSizes,
          (next) => {
            if (!cancelled) setPageSizes(next);
          },
          () => cancelled
        );

        if (cancelled) return;

        setLoading(false);
        const ready = allSizesMeasured(finalSizes);
        setPageSizes(finalSizes);
        setPageSizesReady(ready);
        if (ready) setSizesEpoch(measureEpoch);
      } catch (err: unknown) {
        if (!cancelled) {
          console.error(err);
          setError(parsePdfUserError(err));
          setDoc(null);
          setPageCount(0);
          setPageSizes([]);
          setPageSizesReady(false);
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (loadedDoc) {
        loadedDoc.destroy().catch(() => {});
      }
    };
  }, [file, seedKey]);

  return { doc, pageCount, pageSizes, pageSizesReady, sizesEpoch, loading, error };
}